/**
 * Typed API client.
 *
 * Every type here mirrors a FastAPI response model. Two conventions matter:
 *
 * - Offsets (`start_cp`, `end_cp`) are **Unicode code points** against the canonical
 *   `blocks.text`. Never slice a JavaScript string with them directly; go through
 *   `lib/highlight`.
 * - A citation carries an id and a range and no quote text of its own beyond what the
 *   server resolved. The client never invents evidence; it only asks for it.
 */

export interface Principal {
  user_id: string;
  email: string;
  display_name: string;
  is_admin: boolean;
}

export interface Dataset {
  id: string;
  name: string;
  description: string;
  created_at: string;
  ingest_seq: number;
  role: "owner" | "editor" | "viewer";
  // BCP-47-shaped, or null when unconfigured. Drives query-side name folding
  // (`soulsearch.normalise`) on the substring search path — a dataset with no
  // language folds nothing.
  language: string | null;
}

export interface FileStatus {
  file_id: string;
  original_name: string;
  sha256: string;
  size_bytes: number;
  status: string;
  error: string | null;
  documents: number;
  blocks: number;
}

export interface TableColumn {
  label: string;
  type: "string" | "number" | "date" | "bool";
}

export interface TableProfile {
  kind: "table";
  header_rows: number[];
  columns: Record<string, TableColumn>;
}

export interface TablePreviewRow {
  sheet: string | null;
  row_no: number;
  cells: Record<string, { v: unknown; t: string }>;
  text: string;
}

export interface TablePreview {
  profile: TableProfile;
  rows: TablePreviewRow[];
  total_rows: number;
}

export interface Span {
  start_cp: number;
  end_cp: number;
  quote: string;
}

export interface Hit {
  block_id: string;
  document_id: string;
  file_id: string;
  original_name: string;
  block_index: number;
  text: string;
  locator: Record<string, unknown>;
  rank: number;
  spans: Span[];
  // "exact" everywhere except substring mode, where a hit found only through its
  // folded Ukrainian stem (never the stored text) reports "folded".
  matched_by: "exact" | "folded";
}

export interface Entity {
  annotation_id: string;
  category: string;
  subtype: string | null;
  /** The derived form. Never a replacement for the quote. */
  value_normalized: string;
  /** What the source actually says. This is the evidence; the value above is a claim about it. */
  quote: string;
  block_id: string;
  span_id: string;
  start_cp: number;
  end_cp: number;
  sender_handle: string | null;
  suppressed_reason: string | null;
  /** The lexicon version that produced `suppressed_reason` — absent unless suppressed,
   * so a stale suppression is identifiable rather than silently unchanged. */
  lexicon_version: string | null;
  /** Tier-1's model score. Present for person/location/organisation, absent for tier-0's
   * deterministic categories. Orders the listing; never filters it — no threshold
   * parameter exists on this endpoint. */
  score: number | null;
}

export interface Identity {
  cluster_id: string;
  display_value: string;
  anchor_kind: "identifier" | "full_name" | "human";
  prominence: number;
  /** `proposed` reads as unreviewed. Only a human verdict ever changes this. */
  state: "proposed" | "confirmed" | "rejected";
  member_count: number;
  /** `identity-review-triage` design D5: a presentation-only shape marking —
   * never a filter, never present in the export bundle. Sortable (marked last),
   * never suppressed. */
  non_name_shape: boolean;
  non_name_shape_version: string;
}

export interface IdentityMember {
  member_kind: string;
  member_value: string;
  anchor_kind: "identifier" | "full_name" | "human";
  state: "proposed" | "confirmed" | "rejected";
  link_id: string;
}

export interface IdentityEvidence {
  link_id: string;
  span_id: string;
  block_id: string;
  start_cp: number;
  end_cp: number;
  quote: string;
  anchor_kind: "identifier" | "full_name" | "human";
}

export interface IdentityDetail {
  cluster_id: string;
  display_value: string;
  state: "proposed" | "confirmed" | "rejected";
  members: IdentityMember[];
  evidence: IdentityEvidence[];
  mentions: number;
}

export interface RejectedLinkEvidence {
  span_id: string;
  block_id: string;
  start_cp: number;
  end_cp: number;
  quote: string;
}

/** A rejected link, read from the verdict record directly (design D3,
 * `review-surface-integrity`) — the Rejected filter's audit surface, populated even
 * for a singleton cluster whose only link `entity_members_current` structurally
 * excludes once it is rejected. */
export interface RejectedLink {
  link_id: string;
  cluster_id: string;
  display_value: string;
  member_kind: string;
  member_value: string;
  anchor_kind: "identifier" | "full_name" | "human";
  verdict: "confirmed" | "rejected";
  decided_by: string;
  decided_at: string;
  evidence: RejectedLinkEvidence[];
}

export interface ExcludedCandidateEvidence {
  span_id: string;
  block_id: string;
  start_cp: number;
  end_cp: number;
  quote: string;
}

/** `identity-shape-gate` design D3: a full-name candidate the shape gate excluded at
 * generation time — a recomputable projection, nothing stored. `fired_check` is
 * `shape_gate_lowercase` or `shape_gate_digit`; confirming through the human path
 * creates the identity the gate declined to form automatically. */
export interface ExcludedCandidate {
  pair_key: string;
  display_value: string;
  fired_check: string;
  gate_version: string;
  evidence: ExcludedCandidateEvidence[];
}

/** `identity-review-triage` design D3: a same-person split proposal, inert until a
 * human confirms it. Each side is one of the two identities the rule paired,
 * carrying its own member-span evidence — the same evidence surface the identity
 * detail pane already uses, reused rather than re-invented. */
export interface SuggestionEvidence {
  span_id: string;
  block_id: string;
  start_cp: number;
  end_cp: number;
  quote: string;
}

export interface SuggestionSide {
  cluster_id: string;
  pair_key: string;
  display_value: string;
  prominence: number;
  evidence: SuggestionEvidence[];
}

export interface Suggestion {
  suggestion_id: string;
  rule_class: "declension_bridge" | "asr_variant";
  rule_version: string;
  state: "open" | "confirmed" | "dismissed";
  side_a: SuggestionSide;
  side_b: SuggestionSide;
}

export type EdgeKind = "co_document" | "co_segment";
export type ReviewState = "proposed" | "confirmed" | "rejected";

export interface GraphNode {
  cluster_id: string;
  display_value: string | null;
}

export interface GraphEdge {
  kind: EdgeKind;
  a: string;
  b: string;
  /** The raw, recountable number — a document count or a window-occurrence count.
   * Never replaced by `statistic`, which only orders the list. */
  count: number;
  statistic: number;
  window_seconds: number | null;
  /** `proposed` reads as unreviewed. Only a human verdict ever changes this. */
  review_state: ReviewState;
  decided_by: string | null;
  /** Always `"cooccurrence"` for this edge shape — declared-relations design:
   * every edge on every graph surface carries its source. */
  relation_source: "cooccurrence";
  /** `identity-review-triage` design D4: this edge's two endpoints form an open
   * merge suggestion — "possibly one person," excluded from the default listing
   * behind the `show_suspected` toggle, never dropped. */
  suspected_self_pair: boolean;
}

export interface OrphanedVerdict {
  edge_kind: EdgeKind;
  key_a: string;
  key_b: string;
  verdict: "confirmed" | "rejected";
  decided_by: string;
}

export interface DeclaredArgument {
  span_id: string;
  block_id: string;
  start_cp: number;
  end_cp: number;
  quote: string;
}

export interface DeclaredEdge {
  relation_id: string;
  relation_source: "declared";
  relation_type: string;
  subject: DeclaredArgument;
  object: DeclaredArgument;
  /** An ISO date, or `null` — an undated edge must render with an explicit
   * date-unknown marking, never in the present tense (design D5). */
  valid_as_of: string | null;
  valid_as_of_source: "column" | "document" | null;
  review_state: ReviewState;
}

export interface Graph {
  window_seconds: number;
  nodes: GraphNode[];
  edges: GraphEdge[];
  orphaned_verdicts: OrphanedVerdict[];
  declared_edges: DeclaredEdge[];
}

export interface EdgeEvidenceSpan extends Span {
  span_id: string;
  block_id: string;
}

export interface DocumentEvidence {
  document_id: string;
  spans_a: EdgeEvidenceSpan[];
  spans_b: EdgeEvidenceSpan[];
}

export interface EdgeDetail {
  kind: EdgeKind;
  a: string;
  b: string;
  count: number;
  review_state: ReviewState;
  documents: DocumentEvidence[];
}

export interface ExtractionRun {
  run_id: string;
  status: string;
  producer: string;
  producer_version: string;
  guard_version: string;
  n_shards: number;
  n_shards_done: number;
  n_annotations: number;
  /** Counts of what the guards declined, by reason. Not a count of entities. */
  rejections: Record<string, number>;
  /** Populated only when `status === "failed"` — names the missing piece. */
  error: string | null;
}

export interface Block {
  block_id: string;
  document_id: string;
  file_id: string;
  original_name: string;
  sha256: string;
  media_type: string;
  block_index: number;
  text: string;
  lang: string | null;
  event_ts: string | null;
  locator: Record<string, unknown>;
  document_meta: Record<string, unknown>;
  ingested_at: string;
}

/**
 * How a clause stands. The same five names the server's schemas use.
 *
 * A closed union, deliberately. This was `"grounded" | "gap" | "not_asked" | string`,
 * and the trailing `| string` widened the whole thing back to `string` — which is how
 * `derived` came to exist on the server for a whole round while the renderer, knowing
 * only two names, labelled every derived clause "outside the question". A union that
 * admits anything cannot tell you when you have forgotten a case.
 */
export type ClauseKind = "grounded" | "derived" | "gap" | "not_asked" | "conversational";

export interface Clause {
  text: string;
  kind: ClauseKind;
  evidence: string[];
}

export interface Citation {
  marker: string;
  block_id: string;
  start_cp: number;
  end_cp: number;
  quote: string;
  locator: Record<string, unknown>;
}

export interface Answer {
  chat_id: string;
  message_id: string;
  clauses: Clause[];
  citations: Citation[];
  expanded_terms: string[];
  retries: number;
  model: string;
}

export interface ContextMessage {
  block_id: string;
  seq: number;
  sent_at: string | null;
  sender_handle: string | null;
  kind: string;
  text: string;
  is_target: boolean;
}

export interface ArtifactSummary {
  id: string;
  title: string;
  kind: string;
  version_id: string | null;
  version_no: number | null;
  status: string | null;
  stats: Record<string, unknown> | null;
  stale: boolean;
  corpus_watermark: number | null;
  current_ingest_seq: number;
}

export interface UnitCitation {
  span_id: string;
  block_id: string;
  start_cp: number;
  end_cp: number;
  quote: string;
  block_text: string;
  locator: Record<string, unknown>;
  support_score: number | null;
  // Soft and labelled soft (artifacts.py's own words): the database guarantees a
  // citation is real, never that it supports its claim. Orders and labels; nothing
  // ever filters on it (dossier-artifacts spec, "Confidence is soft, labelled, and
  // never a gate").
  support_method: string | null;
  // identity-dossiers design D4: present exactly when this span is also merge
  // evidence for an `entity_links` row — never a bare boolean with the state left to
  // be inferred, matching the export bundle's own `link_derived`/`review_state`
  // fields (`export/bundle.py::build_bundle`) so the SPA and the export never
  // disagree about whether a merge is reviewed.
  link_derived: boolean;
  review_state: "proposed" | "confirmed" | "rejected" | null;
}

/**
 * A derived unit's stored value.
 *
 * Typed rather than a bare number so a timestamp and a count are never confused, and
 * tagged with the operator that produced it — the number is a fact *about* the
 * citation set, and `op` records which fact.
 */
export interface DerivedValue {
  t: "int" | "ts";
  v: string;
  op: string;
  // identity-dossiers design D3: present only on a dossier's contact row (a
  // re-executable co-occurrence unit) — the edge's review state read fresh at
  // generation time, the G²-or-count statistic that ranked it, and which resolved
  // identity is on the other end.
  review_state?: "proposed" | "confirmed" | "rejected";
  statistic?: number;
  counterpart_cluster_id?: string;
  counterpart_display_value?: string;
  edge_kind?: "co_document" | "co_segment";
}

export interface Unit {
  kind: string;
  origin: string;
  /** The rendered form. Always present for derived units, set from `value.v`. */
  text: string | null;
  value: DerivedValue | null;
  computation: Record<string, unknown> | null;
  citations: UnitCitation[];
  rejected_candidates: Array<{
    span_id: string;
    quote: string;
    block_text: string;
  }>;
}

/**
 * A layout holds *references* to units, never content.
 *
 * Hence `{ u: unitId }` rather than a bare id, and certainly rather than the text
 * itself: units are canonical, and a layout that carried a copy of a cell's value
 * would be a second place for it to be wrong.
 */
export interface UnitRef {
  u: string;
}

export interface Layout {
  type?: string;
  columns?: Array<{ key: string; label: string }>;
  rows?: Array<{ cells: Record<string, UnitRef> }>;
  children?: UnitRef[];
  [key: string]: unknown;
}

export interface ArtifactVersion {
  artifact_id: string;
  version_id: string;
  version_no: number;
  title: string;
  kind: string;
  status: string;
  layout: Layout | null;
  stats: Record<string, unknown> | null;
  generation_meta: Record<string, unknown> | null;
  stale: boolean;
  units: Record<string, Unit>;
}

/**
 * The dossier layout shape (`layout.type === "dossier"`).
 *
 * The closed facet registry (`packages/soulsearch/soulsearch/dossier.py::FACETS`) is
 * not re-declared here — the layout already carries each facet's `label`, so the view
 * never needs its own copy of the vocabulary to render one.
 */
export interface DossierFacet {
  facet: string;
  label: string;
  rows: UnitRef[];
}

export interface DossierLayout {
  type: "dossier";
  identity: string;
  // identity-dossiers design D1: which subject kind produced this layout, so the
  // renderer never has to infer it. Absent on a layout generated before this change
  // — treated as "handle" everywhere it is read.
  subject_kind?: "handle" | "identity_cluster";
  cluster_id?: string;
  facets: DossierFacet[];
  coverage: UnitRef[];
}

export function isDossierLayout(layout: Layout | null): layout is Layout & DossierLayout {
  return !!layout && layout.type === "dossier";
}

/**
 * The inference layout shape (`layout.type === "inference"`, inference-claims task
 * 6.2). `ask` names which of the two producers ran; `units` is every unit this run
 * produced, in emission order — claims/relations first, then exactly one `coverage`
 * unit and whatever `not_asked`/`gap` units the run's gating produced. There is no
 * per-category grouping in the layout itself: the closed category vocabulary lives in
 * the unit's own `value.category`, not in a second copy here.
 */
export interface InferenceLayout {
  type: "inference";
  ask: "soft_claims" | "relations";
  units: UnitRef[];
}

export function isInferenceLayout(layout: Layout | null): layout is Layout & InferenceLayout {
  return !!layout && layout.type === "inference";
}

export interface Provider {
  id: string;
  role: string;
  kind: string;
  label: string;
  base_url: string;
  model: string;
  config: Record<string, unknown>;
  enabled: boolean;
  has_key: boolean;
  last4: string | null;
  last_tested_at: string | null;
  last_test_ok: boolean | null;
  last_test_detail: string | null;
}

export interface AuditEntry {
  id: number;
  occurred_at: string;
  actor_id: string | null;
  action: string;
  dataset_id: string | null;
  detail: Record<string, unknown>;
  entry_hash: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // A multipart upload must NOT carry an explicit Content-Type: only the browser knows
  // the boundary it is about to generate, and setting the header by hand produces a
  // body the server cannot parse.
  const headers: HeadersInit =
    init?.body instanceof FormData
      ? (init.headers ?? {})
      : { "Content-Type": "application/json", ...(init?.headers ?? {}) };

  const response = await fetch(path, { ...init, headers });
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === "string") detail = body.detail;
    } catch {
      // A non-JSON error body is still an error; the status text stands.
    }
    throw new ApiError(response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

export const api = {
  login: (email: string, password: string) =>
    request<Principal>("/api/auth/login", {
      method: "POST",
      ...json({ email, password }),
    }),
  me: () => request<Principal>("/api/auth/me"),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),

  datasets: () => request<Dataset[]>("/api/datasets"),
  dataset: (datasetId: string) => request<Dataset>(`/api/datasets/${datasetId}`),
  createDataset: (name: string, description = "") =>
    request<Dataset>("/api/datasets", {
      method: "POST",
      ...json({ name, description }),
    }),
  updateDataset: (
    datasetId: string,
    fields: { name: string; description: string; language: string | null },
  ) =>
    request<Dataset>(`/api/datasets/${datasetId}`, {
      method: "PATCH",
      ...json(fields),
    }),

  files: (datasetId: string) =>
    request<FileStatus[]>(`/api/datasets/${datasetId}/files`),
  upload: (datasetId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<{
      file_id: string;
      sha256: string;
      already_present: boolean;
    }>(`/api/datasets/${datasetId}/files`, { method: "POST", body: form });
  },

  tableProfilePreview: (datasetId: string, fileId: string) =>
    request<TablePreview>(
      `/api/datasets/${datasetId}/files/${fileId}/table-profile-preview`,
    ),
  previewEditedTableProfile: (datasetId: string, fileId: string, profile: TableProfile) =>
    request<TablePreview>(
      `/api/datasets/${datasetId}/files/${fileId}/table-profile-preview`,
      { method: "POST", ...json({ profile }) },
    ),
  approveTableProfile: (datasetId: string, profile: TableProfile) =>
    request<{
      id: string;
      name: string;
      version: number;
      origin: string;
      spec_hash: string;
      approved_at: string | null;
    }>(`/api/datasets/${datasetId}/profiles`, {
      method: "POST",
      ...json({ profile, origin: "wizard", kind: "table" }),
    }),

  search: (
    datasetId: string,
    q: string,
    mode: "fulltext" | "substring" = "fulltext",
  ) =>
    request<Hit[]>(
      `/api/datasets/${datasetId}/search?q=${encodeURIComponent(q)}&mode=${mode}`,
    ),

  entities: (
    datasetId: string,
    opts: {
      category?: string;
      q?: string;
      includeSuppressed?: boolean;
      /** "score" orders by the model's confidence, highest first. There is
       * deliberately no filter-shaped option here — score triages, it never excludes. */
      sort?: "score";
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.category) params.set("category", opts.category);
    if (opts.q) params.set("q", opts.q);
    if (opts.includeSuppressed) params.set("include_suppressed", "true");
    if (opts.sort) params.set("sort", opts.sort);
    return request<Entity[]>(`/api/datasets/${datasetId}/entities?${params}`);
  },
  startExtraction: (datasetId: string, producer: "tier0" | "tier1" = "tier0") =>
    request<{ run_id: string; n_shards: number; n_blocks: number }>(
      `/api/datasets/${datasetId}/extractions?producer=${producer}`,
      { method: "POST" },
    ),
  extraction: (datasetId: string, runId: string) =>
    request<ExtractionRun>(`/api/datasets/${datasetId}/extractions/${runId}`),

  block: (datasetId: string, blockId: string) =>
    request<Block>(`/api/datasets/${datasetId}/blocks/${blockId}`),

  context: (datasetId: string, blockId: string, n = 12) =>
    request<ContextMessage[]>(
      `/api/datasets/${datasetId}/blocks/${blockId}/context?n=${n}`,
    ),

  ask: (datasetId: string, question: string, chatId?: string | null) =>
    request<Answer>(`/api/datasets/${datasetId}/chat`, {
      method: "POST",
      ...json({ question, chat_id: chatId ?? null }),
    }),

  artifacts: (datasetId: string) =>
    request<ArtifactSummary[]>(`/api/datasets/${datasetId}/artifacts`),
  generate: (
    datasetId: string,
    body: {
      title: string;
      prompt?: string;
      kind?: string;
      spec?: Record<string, unknown>;
    },
  ) =>
    request<{ artifact_id: string; version_id: string; job_id: string }>(
      `/api/datasets/${datasetId}/artifacts`,
      { method: "POST", ...json({ kind: "table", prompt: "", spec: {}, ...body }) },
    ),
  /** `kind: "dossier"` over the same endpoint (dossier-artifacts task 3.2) — an
   * identity, not a prompt, is the spec. `identity-dossiers` design D1 adds a second
   * subject form (`spec.identity_cluster`) beside the original handle form
   * (`spec.identity`); the caller picks one, never both. */
  generateDossier: (
    datasetId: string,
    title: string,
    subject: { kind: "handle"; identity: string } | { kind: "identity_cluster"; clusterId: string },
  ) =>
    request<{ artifact_id: string; version_id: string; job_id: string }>(
      `/api/datasets/${datasetId}/artifacts`,
      {
        method: "POST",
        ...json({
          kind: "dossier",
          title,
          spec:
            subject.kind === "handle"
              ? { identity: subject.identity }
              : { identity_cluster: subject.clusterId },
        }),
      },
    ),
  /** `kind: "inference"` over the same endpoint (inference-claims task 5.5/6.2).
   * `participantOnly` is passed through explicitly rather than defaulted away here —
   * mention-only is the producer's own default (design D6), and this call must never
   * silently narrow it. */
  generateInference: (
    datasetId: string,
    body: {
      title: string;
      ask: "soft_claims" | "relations";
      identity?: string | undefined;
      categories?: string[] | undefined;
      participantOnly: boolean;
    },
  ) =>
    request<{ artifact_id: string; version_id: string; job_id: string }>(
      `/api/datasets/${datasetId}/artifacts`,
      {
        method: "POST",
        ...json({
          kind: "inference",
          title: body.title,
          spec: {
            ask: body.ask,
            identity: body.identity,
            categories: body.categories,
            participant_only: body.participantOnly,
          },
        }),
      },
    ),
  version: (datasetId: string, artifactId: string, versionId: string) =>
    request<ArtifactVersion>(
      `/api/datasets/${datasetId}/artifacts/${artifactId}/versions/${versionId}`,
    ),
  exportUrl: (datasetId: string, artifactId: string, versionId: string) =>
    `/api/datasets/${datasetId}/artifacts/${artifactId}/versions/${versionId}/export`,

  providers: () => request<Provider[]>("/api/admin/providers"),
  putProvider: (body: Record<string, unknown>) =>
    request<Provider>("/api/admin/providers", { method: "PUT", ...json(body) }),
  testProvider: (id: string) =>
    request<{ ok: boolean; detail: string }>(
      `/api/admin/providers/${id}/test`,
      {
        method: "POST",
      },
    ),

  startResolution: (datasetId: string) =>
    request<{ run_id: string; n_blocks: number }>(
      `/api/datasets/${datasetId}/resolutions`,
      { method: "POST" },
    ),
  identities: (datasetId: string, state?: string) =>
    request<Identity[]>(
      `/api/datasets/${datasetId}/identities${state ? `?state=${state}` : ""}`,
    ),
  identity: (datasetId: string, clusterId: string) =>
    request<IdentityDetail>(
      `/api/datasets/${datasetId}/identities/${clusterId}`,
    ),
  rejectedLinks: (datasetId: string) =>
    request<RejectedLink[]>(`/api/datasets/${datasetId}/identities/rejected`),
  submitVerdict: (datasetId: string, linkId: string, verdict: "confirmed" | "rejected") =>
    request<{ link_id: string; state: string; decided_by: string }>(
      `/api/datasets/${datasetId}/links/${linkId}/verdicts`,
      { method: "POST", ...json({ verdict }) },
    ),

  excludedCandidates: (datasetId: string) =>
    request<ExcludedCandidate[]>(`/api/datasets/${datasetId}/identities/excluded`),
  confirmExcludedCandidate: (datasetId: string, pairKey: string) =>
    request<{ cluster_id: string }>(
      `/api/datasets/${datasetId}/identities/excluded/confirm`,
      { method: "POST", ...json({ pair_key: pairKey }) },
    ),

  suggestions: (datasetId: string, state?: "open" | "confirmed" | "dismissed") =>
    request<Suggestion[]>(
      `/api/datasets/${datasetId}/suggestions${state ? `?state=${state}` : ""}`,
    ),
  submitSuggestionVerdict: (
    datasetId: string,
    suggestionId: string,
    verdict: "confirmed" | "dismissed",
  ) =>
    request<{ suggestion_id: string; state: string; cluster_id: string | null }>(
      `/api/datasets/${datasetId}/suggestions/${suggestionId}/verdicts`,
      { method: "POST", ...json({ verdict }) },
    ),

  cooccurrenceGraph: (
    datasetId: string,
    opts: {
      kind?: EdgeKind;
      windowSeconds?: number;
      limit?: number;
      rejected?: boolean;
      showSuspected?: boolean;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.kind) params.set("kind", opts.kind);
    if (opts.windowSeconds) params.set("window_seconds", String(opts.windowSeconds));
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.rejected) params.set("rejected", "true");
    if (opts.showSuspected) params.set("show_suspected", "true");
    const qs = params.toString();
    return request<Graph>(`/api/datasets/${datasetId}/graph/cooccurrence${qs ? `?${qs}` : ""}`);
  },
  cooccurrenceEdge: (
    datasetId: string,
    a: string,
    b: string,
    kind: EdgeKind,
    windowSeconds?: number,
  ) => {
    const params = new URLSearchParams({ a, b, kind });
    if (windowSeconds) params.set("window_seconds", String(windowSeconds));
    return request<EdgeDetail>(
      `/api/datasets/${datasetId}/graph/cooccurrence/edge?${params.toString()}`,
    );
  },
  submitLinkVerdict: (
    datasetId: string,
    body: { kind: EdgeKind; a: string; b: string; verdict: "confirmed" | "rejected" },
  ) =>
    request<{ kind: EdgeKind; a: string; b: string; review_state: ReviewState }>(
      `/api/datasets/${datasetId}/graph/cooccurrence/edge/verdict`,
      { method: "POST", ...json(body) },
    ),
  submitRelationVerdict: (
    datasetId: string,
    relationId: string,
    verdict: "confirmed" | "rejected",
  ) =>
    request<{ annotation_id: string; review_state: ReviewState }>(
      `/api/datasets/${datasetId}/relations/${relationId}/verdicts`,
      { method: "POST", ...json({ verdict }) },
    ),

  audit: (limit = 100) => request<AuditEntry[]>(`/api/audit?limit=${limit}`),
  auditVerify: () =>
    request<{
      entries: number;
      intact: boolean;
      first_break_id: number | null;
      reason: string | null;
    }>("/api/audit/verify"),
};
